import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from './AuthContext';
import { useLang } from './LangContext';
import useBottomBoundedViewport from '../utils/useBottomBoundedViewport';
import useBodyScrollLock from '../utils/useBodyScrollLock';
import Paginator, { usePaginator } from './Paginator';
import '../styles/Training.css';

const POSITIONS = [
  { key: 'mam_non', label: 'Mầm Non' },
  { key: 'tap_su', label: 'Leve' },
  { key: 'culi_cap_cao', label: 'Culi Cấp Cao' },
  { key: 'bo_lao', label: 'Bô Lão' }
];
const DIFFICULTIES = [
  { key: 'easy', label: 'Dễ', color: '#22c55e' },
  { key: 'medium', label: 'Trung bình', color: '#eab308' },
  { key: 'hard', label: 'Khó', color: '#f97316' },
  { key: 'expert', label: 'Chuyên gia', color: '#ef4444' }
];
const STATUS_STYLE = {
  AC: { label: 'Accepted', color: '#22c55e' },
  WA: { label: 'Wrong Answer', color: '#ef4444' },
  TLE: { label: 'Time Limit', color: '#f97316' },
  OLE: { label: 'Output Limit', color: '#f97316' },
  RE: { label: 'Runtime Error', color: '#ef4444' },
  CE: { label: 'Compile Error', color: '#a855f7' },
  SYS: { label: 'System Error', color: '#6b7280' }
};

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
  } catch { return iso; }
}

const STARTER = {
  c: `#include <stdio.h>\n\nint main(void) {\n    // your code here\n    return 0;\n}\n`,
  cpp: `#include <bits/stdc++.h>\nusing namespace std;\n\nint main() {\n    // your code here\n    return 0;\n}\n`
};

// ===== Main component =====
// mode:
//   - 'member' (default): flat problem list + leaderboard + problem view only
//   - 'admin': module management, problem CRUD, records, leaderboard, problem view
const Training = ({ canAdmin, mode = 'member' }) => {
  const lang = useLang();
  const { token } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };
  const initialView = mode === 'admin' ? { name: 'modules' } : { name: 'problems' };
  const [view, setView] = useState(initialView);
  const [modules, setModules] = useState([]);
  const [msg, setMsg] = useState(null);
  const show = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  const refreshModules = async () => {
    try {
      const res = await fetch('/api/training/modules', { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setModules(data.modules || []);
    } catch (err) { show('error', err.message); }
  };

  useEffect(() => { refreshModules(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="training-root">
      {msg && <div className={`training-msg ${msg.type}`}>{msg.text}</div>}

      {/* ---- MEMBER ENTRY POINT ---- */}
      {mode === 'member' && view.name === 'problems' && (
        <MemberProblemsView
          authHeaders={authHeaders}
          onOpenProblem={(p) => setView({ name: 'problem', problemId: p.id, module: { id: p.moduleId, name: p.moduleName, language: p.moduleLanguage } })}
          onViewLeaderboard={() => setView({ name: 'leaderboard-global' })}
          show={show}
        />
      )}
      {mode === 'member' && view.name === 'leaderboard-global' && (
        <LeaderboardView
          module={null}
          authHeaders={authHeaders}
          onBack={() => setView({ name: 'problems' })}
          show={show}
        />
      )}

      {/* ---- ADMIN ENTRY POINT ---- */}
      {mode === 'admin' && view.name === 'modules' && (
        <ModulesView
          modules={modules}
          canAdmin={canAdmin}
          authHeaders={authHeaders}
          onRefresh={refreshModules}
          onOpen={(m) => setView({ name: 'module', module: m })}
          show={show}
        />
      )}
      {mode === 'admin' && view.name === 'module' && (
        <ModuleView
          moduleIn={view.module}
          canAdmin={canAdmin}
          authHeaders={authHeaders}
          onBack={() => { refreshModules(); setView({ name: 'modules' }); }}
          onOpenProblem={(p) => setView({ name: 'problem', problemId: p.id, module: view.module })}
          onViewRecords={() => setView({ name: 'records', module: view.module })}
          onViewLeaderboard={() => setView({ name: 'leaderboard', module: view.module })}
          show={show}
        />
      )}
      {mode === 'admin' && view.name === 'records' && (
        <RecordsView
          module={view.module}
          authHeaders={authHeaders}
          onBack={() => setView({ name: 'module', module: view.module })}
          onOpen={(subId) => setView({ name: 'submission', submissionId: subId, module: view.module })}
          show={show}
        />
      )}
      {mode === 'admin' && view.name === 'leaderboard' && (
        <LeaderboardView
          module={view.module}
          authHeaders={authHeaders}
          onBack={() => setView({ name: 'module', module: view.module })}
          show={show}
        />
      )}
      {mode === 'admin' && view.name === 'submission' && (
        <SubmissionView
          submissionId={view.submissionId}
          authHeaders={authHeaders}
          onBack={() => setView({ name: 'records', module: view.module })}
          show={show}
        />
      )}

      {/* ---- SHARED PROBLEM SOLVE VIEW ---- */}
      {view.name === 'problem' && (
        <ProblemView
          problemId={view.problemId}
          module={view.module}
          canAdmin={canAdmin}
          authHeaders={authHeaders}
          onBack={() => setView(mode === 'admin' ? { name: 'module', module: view.module } : { name: 'problems' })}
          show={show}
        />
      )}
    </div>
  );
};

// ===== Smart code-editor textarea =====
// Tries to mimic VS Code-style behaviour for C/C++:
//   - Tab inserts 4 spaces (or shifts the current selection by 4 spaces)
//   - Shift+Tab dedents 4 spaces
//   - Enter auto-indents to the previous line's indent, +4 spaces if the
//     previous line ends with `{`, `(`, `[` or `:`
//   - Typing `}` automatically dedents one level if the line is nothing but
//     whitespace before it
//   - Auto-close of `(`, `{`, `[`, `"` and `'`
const CodeEditor = ({ value, onChange, rows = 22, language, placeholder, className = 'training-editor' }) => {
  const ref = React.useRef(null);
  const INDENT = '    '; // 4 spaces

  const setSelection = (start, end) => {
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      el.selectionStart = start;
      el.selectionEnd = end == null ? start : end;
    });
  };

  const replaceRange = (start, end, insert, caret) => {
    const next = value.slice(0, start) + insert + value.slice(end);
    onChange(next);
    if (caret != null) setSelection(caret, caret);
  };

  const onKeyDown = (e) => {
    const el = e.target;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const lineStart = before.lastIndexOf('\n') + 1;
    const currentLine = value.slice(lineStart, start);

    // ----- Tab / Shift+Tab -----
    if (e.key === 'Tab') {
      e.preventDefault();
      if (start !== end && value.slice(start, end).includes('\n')) {
        // Indent / dedent block
        const blockStart = before.lastIndexOf('\n') + 1;
        const block = value.slice(blockStart, end);
        const lines = block.split('\n');
        let delta = 0, firstDelta = 0;
        const replaced = lines.map((ln, i) => {
          if (e.shiftKey) {
            const m = ln.match(/^( {1,4}|\t)/);
            if (m) { delta -= m[0].length; if (i === 0) firstDelta = -m[0].length; return ln.slice(m[0].length); }
            return ln;
          }
          if (i === 0) firstDelta = INDENT.length;
          delta += INDENT.length;
          return INDENT + ln;
        }).join('\n');
        const next = value.slice(0, blockStart) + replaced + value.slice(end);
        onChange(next);
        setSelection(start + firstDelta, end + delta);
        return;
      }
      if (e.shiftKey) {
        // Dedent the current line
        const m = currentLine.match(/^( {1,4}|\t)/);
        if (m) {
          const next = value.slice(0, lineStart) + currentLine.slice(m[0].length) + after;
          onChange(next);
          setSelection(Math.max(lineStart, start - m[0].length));
        }
        return;
      }
      replaceRange(start, end, INDENT, start + INDENT.length);
      return;
    }

    // ----- Enter: auto-indent -----
    if (e.key === 'Enter') {
      const indentMatch = currentLine.match(/^[ \t]*/);
      let indent = indentMatch ? indentMatch[0] : '';
      const trimmed = currentLine.trimEnd();
      const lastChar = trimmed.slice(-1);
      const nextChar = after.charAt(0);
      const opensBlock = ['{', '(', '[', ':'].includes(lastChar);
      if (opensBlock) {
        e.preventDefault();
        const inner = indent + INDENT;
        // If next char is the matching closing brace, place it on its own line
        const closer = ({ '{': '}', '(': ')', '[': ']' })[lastChar];
        if (closer && nextChar === closer) {
          const insert = '\n' + inner + '\n' + indent;
          replaceRange(start, end, insert, start + 1 + inner.length);
        } else {
          replaceRange(start, end, '\n' + inner, start + 1 + inner.length);
        }
        return;
      }
      if (indent) {
        e.preventDefault();
        replaceRange(start, end, '\n' + indent, start + 1 + indent.length);
        return;
      }
    }

    // ----- Closing brace dedent -----
    if (e.key === '}') {
      // If current line is only whitespace, dedent one level then add `}`
      if (/^[ \t]*$/.test(currentLine) && currentLine.length >= INDENT.length) {
        e.preventDefault();
        const newLine = currentLine.slice(0, currentLine.length - INDENT.length) + '}';
        const next = value.slice(0, lineStart) + newLine + after;
        onChange(next);
        setSelection(lineStart + newLine.length);
        return;
      }
    }

    // ----- Auto-close brackets / quotes -----
    const pairs = { '(': ')', '{': '}', '[': ']', '"': '"', "'": "'" };
    if (pairs[e.key]) {
      // If text is selected, wrap it
      if (start !== end) {
        e.preventDefault();
        const wrapped = e.key + value.slice(start, end) + pairs[e.key];
        replaceRange(start, end, wrapped, start + 1 + (end - start) + 1);
        return;
      }
      // Skip duplicate closer typing
      if ((e.key === '"' || e.key === "'") && after.charAt(0) === e.key) {
        e.preventDefault();
        setSelection(start + 1);
        return;
      }
      e.preventDefault();
      replaceRange(start, end, e.key + pairs[e.key], start + 1);
      return;
    }
    // Skip over auto-inserted closing bracket
    if ([')', ']', '}'].includes(e.key) && after.charAt(0) === e.key) {
      e.preventDefault();
      setSelection(start + 1);
      return;
    }
    // Backspace inside an empty bracket pair removes both
    if (e.key === 'Backspace' && start === end && start > 0) {
      const ch = value.charAt(start - 1);
      const nx = value.charAt(start);
      if (pairs[ch] && pairs[ch] === nx) {
        e.preventDefault();
        const next = value.slice(0, start - 1) + value.slice(start + 1);
        onChange(next);
        setSelection(start - 1);
        return;
      }
    }
  };

  return (
    <textarea
      ref={ref}
      className={className}
      spellCheck={false}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      rows={rows}
      placeholder={placeholder}
      data-language={language}
    />
  );
};


const MemberProblemsView = ({ authHeaders, onOpenProblem, onViewLeaderboard, show }) => {
  const lang = useLang();
  const [problems, setProblems] = useState([]);
  const [filter, setFilter] = useState({ difficulty: '', tag: '', module: '' });
  const [sortBy, setSortBy] = useState('difficulty');

  const load = async () => {
    try {
      const res = await fetch('/api/training/problems', { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setProblems(data.problems || []);
    } catch (err) { show('error', err.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const displayed = useMemo(() => {
    let arr = [...problems];
    if (filter.difficulty) arr = arr.filter(p => p.difficulty === filter.difficulty);
    if (filter.tag) arr = arr.filter(p => Array.isArray(p.tags) && p.tags.some(t => t.toLowerCase().includes(filter.tag.toLowerCase())));
    if (filter.module) arr = arr.filter(p => String(p.moduleId) === filter.module);
    const dOrd = { easy: 0, medium: 1, hard: 2, expert: 3 };
    if (sortBy === 'difficulty') arr.sort((a, b) => (dOrd[a.difficulty] ?? 9) - (dOrd[b.difficulty] ?? 9) || a.title.localeCompare(b.title));
    else if (sortBy === 'title') arr.sort((a, b) => a.title.localeCompare(b.title));
    else if (sortBy === 'score') arr.sort((a, b) => (b.bestScorePct || 0) - (a.bestScorePct || 0));
    else if (sortBy === 'module') arr.sort((a, b) => a.moduleName.localeCompare(b.moduleName) || a.title.localeCompare(b.title));
    return arr;
  }, [problems, filter, sortBy]);

  const allTags = useMemo(() => {
    const set = new Set();
    problems.forEach(p => Array.isArray(p.tags) && p.tags.forEach(t => set.add(t)));
    return Array.from(set).sort();
  }, [problems]);
  const allModules = useMemo(() => {
    const map = new Map();
    problems.forEach(p => map.set(p.moduleId, p.moduleName));
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [problems]);

  return (
    <div>
      <div className="training-header">
        <div>
          <h2 className="training-title">{lang.TRAINING_TITLE || 'Training'}</h2>
          <p className="training-sub">{lang.TRAINING_MEMBER_SUB || 'Danh sách bài tập'}</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={onViewLeaderboard}>{lang.TRAINING_TOP_SOLVERS || 'Top giải bài'}</button>
      </div>

      <div className="training-filters">
        <label>Nhóm bài
          <select value={filter.module} onChange={e => setFilter({ ...filter, module: e.target.value })}>
            <option value="">Tất cả</option>
            {allModules.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </label>
        <label>Độ khó
          <select value={filter.difficulty} onChange={e => setFilter({ ...filter, difficulty: e.target.value })}>
            <option value="">Tất cả</option>
            {DIFFICULTIES.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
        </label>
        <label>Tag
          <select value={filter.tag} onChange={e => setFilter({ ...filter, tag: e.target.value })}>
            <option value="">Tất cả</option>
            {allTags.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label>Sắp xếp
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="difficulty">Độ khó</option>
            <option value="module">Nhóm bài</option>
            <option value="title">Tiêu đề</option>
            <option value="score">Điểm của tôi</option>
          </select>
        </label>
      </div>

      <PaginatedTable
        rows={displayed}
        defaultSize={10}
        renderTable={(paged, offset) => (
          <table className="training-problem-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Tiêu đề</th>
                <th>Nhóm bài</th>
                <th>Độ khó</th>
                <th>Tags</th>
                <th>Test cases</th>
                <th>Điểm của tôi</th>
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 && <tr><td colSpan="7" className="empty-row">Chưa có bài tập nào.</td></tr>}
              {paged.map((p, i) => {
                const diff = DIFFICULTIES.find(d => d.key === p.difficulty) || DIFFICULTIES[0];
                return (
                  <tr key={p.id}>
                    <td>{offset + i + 1}</td>
                    <td><button className="link-button" onClick={() => onOpenProblem(p)}>{p.title}</button></td>
                    <td>{p.moduleName}</td>
                    <td><span className="training-diff-tag" style={{ background: diff.color + '22', color: diff.color, borderColor: diff.color }}>{diff.label}</span></td>
                    <td>{Array.isArray(p.tags) && p.tags.map(t => <span key={t} className="training-chip">{t}</span>)}</td>
                    <td>{p.testcaseCount != null ? p.testcaseCount : '-'}</td>
                    <td>
                      {p.bestScorePct == null ? <span className="training-muted">{lang.TRAINING_NOT_SUBMITTED || 'Chưa nộp'}</span>
                        : p.bestScorePct >= 100 ? <span className="training-ac">✓ 100%</span>
                        : <span>{p.bestScorePct.toFixed(1)}%</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      />
    </div>
  );
};


// PaginatedTable — wraps any list+table render fn with the shared Paginator.
// We use a render-prop because each table layout has a different column
// schema; this keeps the slicing logic in one place without forcing every
// caller to copy-paste the boilerplate around <Paginator />.
const PaginatedTable = ({ rows, defaultSize = 10, renderTable }) => {
  const pg = usePaginator(rows, defaultSize);
  const offset = pg.pageSize === 0 ? 0 : (pg.page - 1) * pg.pageSize;
  return (
    <>
      <Paginator total={pg.total} page={pg.page} pageSize={pg.pageSize} onChange={pg.onChange} position="top" />
      {renderTable(pg.paged, offset)}
      <Paginator total={pg.total} page={pg.page} pageSize={pg.pageSize} onChange={pg.onChange} position="bottom" />
    </>
  );
};


// ===== Modules list =====
const ModulesView = ({ modules, canAdmin, authHeaders, onRefresh, onOpen, show }) => {
  const lang = useLang();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', language: 'cpp', allowedPositions: [] });

  const startEdit = (m) => {
    setEditing(m);
    setForm({
      name: m.name,
      description: m.description || '',
      language: m.language || 'cpp',
      allowedPositions: Array.isArray(m.allowedPositions) ? [...m.allowedPositions] : []
    });
    setShowForm(true);
  };
  const resetForm = () => {
    setEditing(null);
    setForm({ name: '', description: '', language: 'cpp', allowedPositions: [] });
    setShowForm(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const url = editing ? `/api/training/modules/${editing.id}` : '/api/training/modules';
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      show('success', editing ? 'Đã cập nhật module.' : 'Đã tạo module.');
      resetForm();
      onRefresh();
    } catch (err) { show('error', err.message); }
  };
  const delMod = async (m) => {
    if (!window.confirm(lang.TRAINING_MODULE_DELETE_CONFIRM || `Xoá module "${m.name}" (kèm toàn bộ bài & nộp bài)?`)) return;
    try {
      const res = await fetch(`/api/training/modules/${m.id}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      show('success', 'Đã xoá.');
      onRefresh();
    } catch (err) { show('error', err.message); }
  };

  return (
    <div>
      <div className="training-header">
        <div>
          <h2 className="training-title">Training — Online Judge</h2>
        </div>
        {canAdmin && !editing && (
          <button className="btn btn-primary btn-sm" onClick={() => { setShowForm(!showForm); setEditing(null); }}>
            + Tạo Module Mới
          </button>
        )}
      </div>

      {showForm && canAdmin && (
        <form className="training-form" onSubmit={submit}>
          <h3>{editing ? 'Chỉnh sửa module' : 'Module mới'}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>{lang.TRAINING_MODULE_NAME || 'Tên module *'}</label>
              <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>{lang.TRAINING_DEFAULT_LANG || 'Ngôn ngữ mặc định'}</label>
              <select value={form.language} onChange={e => setForm({ ...form, language: e.target.value })}>
                <option value="cpp">C++</option>
                <option value="c">C</option>
              </select>
            </div>
            <div className="form-group form-group-full">
              <label>{lang.COMMON_DESCRIPTION || 'Mô tả'}</label>
              <textarea rows="3" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="form-group form-group-full">
              <label>{lang.TRAINING_VISIBLE_LEVELS || 'Cho phép cấp thành viên xem'}</label>
              <div className="checkbox-row">
                {POSITIONS.map(p => (
                  <label key={p.key} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.allowedPositions.includes(p.key)}
                      onChange={() => {
                        const has = form.allowedPositions.includes(p.key);
                        setForm({
                          ...form,
                          allowedPositions: has
                            ? form.allowedPositions.filter(x => x !== p.key)
                            : [...form.allowedPositions, p.key]
                        });
                      }}
                    />
                    {p.label}
                  </label>
                ))}
              </div>
              <p className="form-hint">{lang.TRAINING_LEVEL_HINT || 'Chỉ những cấp thành viên được tick mới thấy module này (admin luôn thấy hết).'}</p>
            </div>
          </div>
          <div className="training-form-actions">
            <button type="submit" className="btn btn-primary btn-sm">{editing ? 'Cập nhật' : 'Tạo'}</button>
            <button type="button" className="btn btn-outline btn-sm" onClick={resetForm}>{lang.COMMON_CANCEL || 'Huỷ'}</button>
          </div>
        </form>
      )}

      {modules.length === 0 && !editing && !showForm && (
        <p className="empty-text">Chưa có module nào hiển thị với bạn.</p>
      )}

      {!editing && !showForm && (
      <div className="training-modules-grid">
        {modules.map(m => (
          <div key={m.id} className="training-module-card">
            <div className="training-module-head">
              <h3 onClick={() => onOpen(m)} style={{ cursor: 'pointer' }}>{m.name}</h3>
              <span className={`training-lang-tag training-lang-${m.language}`}>{m.language.toUpperCase()}</span>
            </div>
            {m.description && <p className="training-module-desc">{m.description}</p>}
            <div className="training-module-meta">
              <span>📝 {m.problemCount || 0} bài</span>
              {Array.isArray(m.allowedPositions) && m.allowedPositions.length > 0 && (
                <span>👥 {m.allowedPositions.map(p => POSITIONS.find(x => x.key === p)?.label || p).join(', ')}</span>
              )}
            </div>
            <div className="training-module-actions">
              <button className="btn btn-primary btn-sm" onClick={() => onOpen(m)}>Mở</button>
              {canAdmin && (
                <>
                  <button className="btn btn-outline btn-sm" onClick={() => startEdit(m)}>Sửa</button>
                  <button className="btn btn-danger btn-sm" onClick={() => delMod(m)}>Xoá</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
};

// ===== Module detail: problem list + admin =====
const ModuleView = ({ moduleIn, canAdmin, authHeaders, onBack, onOpenProblem, onViewRecords, onViewLeaderboard, show }) => {
  const lang = useLang();
  const [module, setModule] = useState(moduleIn);
  const [problems, setProblems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState({ difficulty: '', tag: '' });
  const [sortBy, setSortBy] = useState('difficulty');

  const emptyForm = () => ({
    title: '', statement: '', difficulty: 'easy', tags: '',
    timeLimitMs: 2000, memoryLimitMb: 256,
    shareCodeThresholdPct: 100,
    shareCodeThresholdMode: 'percent',
    shareCodeThresholdTestcases: 1,
    compareMode: 'token',
    language: module.language || 'cpp',
    testcases: [{ input: '', output: '', score: 1 }]
  });
  const [form, setForm] = useState(emptyForm());

  const refresh = async () => {
    try {
      const res = await fetch(`/api/training/modules/${moduleIn.id}/problems`, { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setModule(data.module);
      setProblems(data.problems || []);
    } catch (err) { show('error', err.message); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [moduleIn.id]);

  const startEdit = async (p) => {
    // Fetch full problem with testcases (admin)
    try {
      const res = await fetch(`/api/training/problems/${p.id}`, { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      const full = data.problem;
      setEditing(full);
      setForm({
        title: full.title,
        statement: full.statement || '',
        difficulty: full.difficulty || 'easy',
        tags: Array.isArray(full.tags) ? full.tags.join(', ') : '',
        timeLimitMs: full.timeLimitMs || 2000,
        memoryLimitMb: full.memoryLimitMb || 256,
        shareCodeThresholdPct: typeof full.shareCodeThresholdPct === 'number' ? full.shareCodeThresholdPct : 100,
        shareCodeThresholdMode: full.shareCodeThresholdMode === 'testcases' ? 'testcases' : 'percent',
        shareCodeThresholdTestcases: typeof full.shareCodeThresholdTestcases === 'number' ? full.shareCodeThresholdTestcases : (Array.isArray(full.testcases) ? full.testcases.length : 1),
        compareMode: full.compareMode === 'strict' ? 'strict' : 'token',
        language: full.language || module.language || 'cpp',
        testcases: Array.isArray(full.testcases) && full.testcases.length > 0
          ? full.testcases.map(t => ({ ...t, score: t.score || 1 }))
          : [{ input: '', output: '', score: 1 }]
      });
      setShowForm(true);
    } catch (err) { show('error', err.message); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        moduleId: moduleIn.id,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        timeLimitMs: parseInt(form.timeLimitMs) || 2000,
        memoryLimitMb: parseInt(form.memoryLimitMb) || 256,
        shareCodeThresholdPct: parseInt(form.shareCodeThresholdPct) || 100,
        shareCodeThresholdMode: form.shareCodeThresholdMode === 'testcases' ? 'testcases' : 'percent',
        shareCodeThresholdTestcases: parseInt(form.shareCodeThresholdTestcases) || 0,
        compareMode: form.compareMode === 'strict' ? 'strict' : 'token',
        testcases: form.testcases.filter(t => t.input !== undefined && t.output !== undefined)
      };
      const url = editing ? `/api/training/problems/${editing.id}` : '/api/training/problems';
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      show('success', editing ? 'Đã cập nhật bài.' : 'Đã tạo bài.');
      setEditing(null); setShowForm(false); setForm(emptyForm());
      refresh();
    } catch (err) { show('error', err.message); }
  };
  const del = async (p) => {
    if (!window.confirm(lang.TRAINING_PROBLEM_DELETE_CONFIRM || `Xoá bài "${p.title}"?`)) return;
    try {
      const res = await fetch(`/api/training/problems/${p.id}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      refresh();
    } catch (err) { show('error', err.message); }
  };

  const displayed = useMemo(() => {
    let arr = [...problems];
    if (filter.difficulty) arr = arr.filter(p => p.difficulty === filter.difficulty);
    if (filter.tag) arr = arr.filter(p => Array.isArray(p.tags) && p.tags.some(t => t.toLowerCase().includes(filter.tag.toLowerCase())));
    const dOrd = { easy: 0, medium: 1, hard: 2, expert: 3 };
    if (sortBy === 'difficulty') arr.sort((a, b) => (dOrd[a.difficulty] ?? 9) - (dOrd[b.difficulty] ?? 9) || a.title.localeCompare(b.title));
    else if (sortBy === 'title') arr.sort((a, b) => a.title.localeCompare(b.title));
    else if (sortBy === 'score') arr.sort((a, b) => (b.bestScorePct || 0) - (a.bestScorePct || 0));
    return arr;
  }, [problems, filter, sortBy]);

  const allTags = useMemo(() => {
    const set = new Set();
    problems.forEach(p => Array.isArray(p.tags) && p.tags.forEach(t => set.add(t)));
    return Array.from(set).sort();
  }, [problems]);

  return (
    <div>
      <div className="training-header">
        <div>
          <button className="btn btn-outline btn-sm" onClick={onBack}>← Quay lại</button>
          <h2 className="training-title" style={{ marginTop: 8 }}>{module.name}</h2>
          {module.description && <p className="training-sub">{module.description}</p>}
        </div>
        <div className="training-module-actions">
          <button className="btn btn-outline btn-sm" onClick={onViewRecords}>📜 Lịch sử nộp</button>
          <button className="btn btn-outline btn-sm" onClick={onViewLeaderboard}>🏆 Top</button>
          {canAdmin && !editing && (
            <button className="btn btn-primary btn-sm" onClick={() => { setShowForm(!showForm); setEditing(null); setForm(emptyForm()); }}>
              + Thêm bài
            </button>
          )}
        </div>
      </div>

      {showForm && canAdmin && (
        <ProblemForm
          form={form} setForm={setForm}
          editing={editing}
          submit={submit}
          onCancel={() => { setShowForm(false); setEditing(null); setForm(emptyForm()); }}
        />
      )}

      {!editing && (
      <>
      <div className="training-filters">
        <label>Độ khó
          <select value={filter.difficulty} onChange={e => setFilter({ ...filter, difficulty: e.target.value })}>
            <option value="">Tất cả</option>
            {DIFFICULTIES.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
        </label>
        <label>Tag
          <select value={filter.tag} onChange={e => setFilter({ ...filter, tag: e.target.value })}>
            <option value="">Tất cả</option>
            {allTags.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label>Sắp xếp
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="difficulty">Độ khó</option>
            <option value="title">Tiêu đề</option>
            <option value="score">Điểm của tôi</option>
          </select>
        </label>
      </div>

      <PaginatedTable
        rows={displayed}
        defaultSize={10}
        renderTable={(paged, offset) => (
          <table className="training-problem-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Tiêu đề</th>
                <th>Độ khó</th>
                <th>Tags</th>
                <th>Test cases</th>
                <th>Điểm của tôi</th>
                {canAdmin && <th></th>}
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 && (
                <tr><td colSpan={canAdmin ? 7 : 6} className="empty-row">Chưa có bài nào.</td></tr>
              )}
              {paged.map((p, i) => {
                const diff = DIFFICULTIES.find(d => d.key === p.difficulty) || DIFFICULTIES[0];
                return (
                  <tr key={p.id}>
                    <td>{offset + i + 1}</td>
                    <td>
                      <button className="link-button" onClick={() => onOpenProblem(p)}>{p.title}</button>
                    </td>
                    <td><span className="training-diff-tag" style={{ background: diff.color + '22', color: diff.color, borderColor: diff.color }}>{diff.label}</span></td>
                    <td>
                      {Array.isArray(p.tags) && p.tags.map(t => <span key={t} className="training-chip">{t}</span>)}
                    </td>
                    <td>{p.testcaseCount != null ? p.testcaseCount : (p.testcases ? p.testcases.length : '-')}</td>
                    <td>
                      {p.bestScorePct == null ? <span className="training-muted">{lang.TRAINING_NOT_SUBMITTED || 'Chưa nộp'}</span>
                        : p.bestScorePct >= 100 ? <span className="training-ac">✓ 100%</span>
                        : <span>{p.bestScorePct.toFixed(1)}%</span>}
                    </td>
                    {canAdmin && (
                      <td>
                        <button className="btn btn-outline btn-sm" onClick={() => startEdit(p)}>Sửa</button>
                        <button className="btn btn-danger btn-sm" onClick={() => del(p)}>Xoá</button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      />
      </>
      )}
    </div>
  );
};

// ===== Problem admin form =====
const ProblemForm = ({ form, setForm, editing, submit, onCancel }) => {
  const lang = useLang();
  const updateTc = (i, patch) => {
    const next = [...form.testcases];
    next[i] = { ...next[i], ...patch };
    setForm({ ...form, testcases: next });
  };
  const addTc = () => setForm({ ...form, testcases: [...form.testcases, { input: '', output: '', score: 1 }] });
  const rmTc = (i) => setForm({ ...form, testcases: form.testcases.filter((_, idx) => idx !== i) });

  return (
    <form className="training-form" onSubmit={submit}>
      <h3>{editing ? 'Chỉnh sửa bài toán' : 'Tạo bài toán mới'}</h3>
      <div className="form-grid">
        <div className="form-group form-group-full">
          <label>{lang.COMMON_TITLE || 'Tiêu đề *'}</label>
          <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
        </div>
        <div className="form-group form-group-full">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <label style={{ margin: 0 }}>{lang.TRAINING_STATEMENT_LABEL || 'Đề bài (Markdown — editor & preview)'}</label>
            <span className="form-hint" style={{ margin: 0 }}>{lang.TRAINING_GFM_HINT || 'Hỗ trợ GFM: bảng, code-block, ảnh, in đậm, link…'}</span>
          </div>
          <div className="training-md-split">
            <textarea
              rows="14"
              value={form.statement}
              onChange={e => setForm({ ...form, statement: e.target.value })}
              placeholder={`# Đề bài\n\nMô tả bài toán...\n\n## Input\n- ...\n\n## Output\n- ...\n\n## Ví dụ\n\`\`\`\nInput\n...\nOutput\n...\n\`\`\``}
            />
            <div className="training-md-preview">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {form.statement || (lang.TRAINING_MD_PREVIEW_EMPTY || '_(Trống — gõ ở ô bên trái để xem preview)_')}
              </ReactMarkdown>
            </div>
          </div>
        </div>
        <div className="form-group">
          <label>{lang.TRAINING_ALLOWED_LANGS || 'Ngôn ngữ cho phép'}</label>
          <select value={form.language} onChange={e => setForm({ ...form, language: e.target.value })}>
            <option value="cpp">C++</option>
            <option value="c">C</option>
          </select>
        </div>
        <div className="form-group">
          <label>{lang.TRAINING_DIFFICULTY || 'Độ khó'}</label>
          <select value={form.difficulty} onChange={e => setForm({ ...form, difficulty: e.target.value })}>
            {DIFFICULTIES.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>{lang.TRAINING_TAGS_LABEL || 'Tags (cách nhau bằng dấu phẩy)'}</label>
          <input type="text" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder={lang.TRAINING_TAGS_PH || 'implementation, dp, graph'} />
        </div>
        <div className="form-group">
          <label>{lang.TRAINING_TIME_LIMIT || 'Time limit (ms)'}</label>
          <input type="number" min="100" max="10000" value={form.timeLimitMs} onChange={e => setForm({ ...form, timeLimitMs: e.target.value })} />
        </div>
        <div className="form-group">
          <label>{lang.TRAINING_MEMORY || 'Memory (MB)'}</label>
          <input type="number" min="16" max="1024" value={form.memoryLimitMb} onChange={e => setForm({ ...form, memoryLimitMb: e.target.value })} />
        </div>
        <div className="form-group form-group-full">
          <label>{lang.TRAINING_VIEW_CODE_THRESHOLD || 'Ngưỡng xem code bài người khác'}</label>
          <select value={form.shareCodeThresholdMode}
                  onChange={e => setForm({ ...form, shareCodeThresholdMode: e.target.value })}>
            <option value="percent">Theo phần trăm điểm (%)</option>
            <option value="testcases">Theo số testcase đúng</option>
          </select>
          {form.shareCodeThresholdMode === 'testcases' ? (
            <>
              <input type="number" min="0" max={form.testcases.length || 50}
                     value={form.shareCodeThresholdTestcases}
                     onChange={e => setForm({ ...form, shareCodeThresholdTestcases: e.target.value })}
                     style={{ marginTop: 8 }} />
              <p className="form-hint">
                Khi bạn làm đúng ≥ số testcase này (trên tổng {form.testcases.length} testcase đã setup) cho bài toán, bạn được phép xem code đã AC của người khác.
                Ví dụ: đúng 1 / 2 testcase sẽ cho phép mầm non xem code.
              </p>
            </>
          ) : (
            <>
              <input type="number" min="0" max="100" value={form.shareCodeThresholdPct}
                     onChange={e => setForm({ ...form, shareCodeThresholdPct: e.target.value })}
                     style={{ marginTop: 8 }} />
              <p className="form-hint">{lang.TRAINING_THRESHOLD_HINT || 'Khi bạn đạt ≥ ngưỡng phần trăm điểm này trên bài, bạn được phép xem code của người khác.'}</p>
            </>
          )}
        </div>
        <div className="form-group form-group-full">
          <label>{lang.TRAINING_COMPARE_MODE || 'Cách so sánh output'}</label>
          <select value={form.compareMode} onChange={e => setForm({ ...form, compareMode: e.target.value })}>
            <option value="token">Token — bỏ qua khoảng trắng thừa / xuống dòng cuối (mặc định)</option>
            <option value="strict">Strict — đúng từng byte (giữ nguyên dấu cách &amp; dấu xuống dòng)</option>
          </select>
          <p className="form-hint">
            Dùng <strong>Strict</strong> cho bài xử lý chuỗi / định dạng khi mỗi khoảng trắng và ký tự xuống dòng đều có ý nghĩa.
            <strong> Token</strong> chấp nhận khoảng trắng thừa cuối dòng và newline cuối file.
          </p>
        </div>
      </div>

      <h4 style={{ marginTop: 16 }}>{lang.TRAINING_TEST_CASES || 'Test cases'}</h4>
      <div className="training-testcases">
        {form.testcases.map((tc, i) => (
          <div key={i} className="training-testcase">
            <div className="tc-header">
              <strong>Test #{i + 1}</strong>
              <label>Điểm: <input type="number" min="1" style={{ width: 80 }} value={tc.score}
                                  onChange={e => updateTc(i, { score: parseInt(e.target.value) || 1 })} /></label>
              {form.testcases.length > 1 && (
                <button type="button" className="btn btn-danger btn-sm" onClick={() => rmTc(i)}>{lang.COMMON_DELETE || 'Xoá'}</button>
              )}
            </div>
            <div className="tc-io">
              <label>
                <span>{lang.TRAINING_INPUT || 'Input'}</span>
                <textarea rows="4" value={tc.input} onChange={e => updateTc(i, { input: e.target.value })} />
              </label>
              <label>
                <span>{lang.TRAINING_EXPECTED_OUTPUT || 'Expected Output'}</span>
                <textarea rows="4" value={tc.output} onChange={e => updateTc(i, { output: e.target.value })} />
              </label>
            </div>
          </div>
        ))}
        <button type="button" className="btn btn-outline btn-sm" onClick={addTc}>+ Thêm test case</button>
      </div>

      <div className="training-form-actions">
        <button type="submit" className="btn btn-primary btn-sm">{editing ? 'Cập nhật' : 'Tạo'}</button>
        <button type="button" className="btn btn-outline btn-sm" onClick={onCancel}>{lang.COMMON_CANCEL || 'Huỷ'}</button>
      </div>
    </form>
  );
};

// ===== Problem detail + submit code =====
const ProblemView = ({ problemId, module, canAdmin, authHeaders, onBack, show }) => {
  const lang = useLang();
  const [problem, setProblem] = useState(null);
  const [language, setLanguage] = useState(module.language || 'cpp');
  const [source, setSource] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [subs, setSubs] = useState([]);

  const loadAll = async () => {
    try {
      const [probRes, subsRes] = await Promise.all([
        fetch(`/api/training/problems/${problemId}`, { headers: authHeaders }),
        fetch(`/api/training/problems/${problemId}/submissions`, { headers: authHeaders })
      ]);
      const probData = await probRes.json();
      if (!probRes.ok) throw new Error(probData.error);
      const subsData = await subsRes.json();
      setProblem(probData.problem);
      setLanguage(probData.problem.language || module.language || 'cpp');
      setSubs(subsData.submissions || []);
      if (!source) setSource(STARTER[probData.problem.language || module.language || 'cpp']);
    } catch (err) { show('error', err.message); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [problemId]);

  useEffect(() => {
    // Update starter if user switches language and hasn't typed
    if (!source || Object.values(STARTER).includes(source)) setSource(STARTER[language]);
    // eslint-disable-next-line
  }, [language]);

  const submit = async () => {
    setSubmitting(true); setResult(null);
    try {
      const res = await fetch(`/api/training/problems/${problemId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ source, language })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setResult(data.submission);
      loadAll();
    } catch (err) { show('error', err.message); }
    finally { setSubmitting(false); }
  };

  if (!problem) return <div><button className="btn btn-outline btn-sm" onClick={onBack}>← Quay lại</button><p>Đang tải...</p></div>;

  const diff = DIFFICULTIES.find(d => d.key === problem.difficulty) || DIFFICULTIES[0];

  return (
    <div className="training-problem-view">
      <button className="btn btn-outline btn-sm" onClick={onBack}>← Quay lại danh sách</button>
      <div className="training-problem-head">
        <h2 className="training-title">📝 {problem.title}</h2>
        <div className="training-problem-meta">
          <span className="training-diff-tag" style={{ background: diff.color + '22', color: diff.color, borderColor: diff.color }}>{diff.label}</span>
          <span>⏱ {problem.timeLimitMs} ms</span>
          <span>💾 {problem.memoryLimitMb} MB</span>
          <span>🔢 {problem.testcaseCount ?? (problem.testcases?.length || '?')} test cases</span>
          <span title="Cách so sánh output" style={{ background: problem.compareMode === 'strict' ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)', color: problem.compareMode === 'strict' ? '#ef4444' : '#22c55e', padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 600 }}>
            {problem.compareMode === 'strict' ? 'So sánh STRICT' : 'So sánh TOKEN'}
          </span>
          {Array.isArray(problem.tags) && problem.tags.map(t => <span key={t} className="training-chip">{t}</span>)}
        </div>
      </div>

      <div className="training-split">
        <div className="training-statement">
          <h3>{lang.TRAINING_STATEMENT || 'Đề bài'}</h3>
          <div className="training-statement-md">
            {problem.statement
              ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{problem.statement}</ReactMarkdown>
              : <p className="training-muted">(Không có đề bài.)</p>}
          </div>
        </div>
        <div className="training-editor-panel">
          <div className="training-editor-head">
            <label>Ngôn ngữ:
              <select value={language} onChange={e => setLanguage(e.target.value)}>
                <option value="cpp">C++ (g++)</option>
                <option value="c">C (gcc)</option>
              </select>
            </label>
            <label className="btn btn-outline btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
              📁 Tải file
              <input
                type="file"
                accept=".c,.cpp,.cc,.cxx,.h,.hpp,.txt,text/*"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const f = e.target.files && e.target.files[0];
                  if (!f) return;
                  if (f.size > 512 * 1024) { show('error', 'File quá lớn (>512KB)'); e.target.value = ''; return; }
                  try {
                    const text = await f.text();
                    setSource(text);
                    // Guess language from extension
                    const lower = f.name.toLowerCase();
                    if (lower.endsWith('.c')) setLanguage('c');
                    else if (lower.endsWith('.cpp') || lower.endsWith('.cc') || lower.endsWith('.cxx')) setLanguage('cpp');
                  } catch (err) { show('error', 'Không đọc được file: ' + err.message); }
                  e.target.value = '';
                }}
              />
            </label>
            <button className="btn btn-primary btn-sm" disabled={submitting} onClick={submit}>
              {submitting ? 'Đang chấm...' : '📤 Nộp bài'}
            </button>
          </div>
          <CodeEditor
            value={source}
            onChange={setSource}
            rows={22}
            language={language}
          />
          {result && <SubmissionResultCard submission={result} />}
        </div>
      </div>

      <h3 style={{ marginTop: 20 }}>Bài nộp của {canAdmin ? 'tất cả' : 'bạn'}</h3>
      <SubmissionList subs={subs} authHeaders={authHeaders} show={show} canAdmin={canAdmin} onReload={loadAll} />
    </div>
  );
};

// ===== Result card =====
const SubmissionResultCard = ({ submission }) => {
  const style = STATUS_STYLE[submission.status] || STATUS_STYLE.SYS;
  // UX: for Runtime / System errors we want to surface the first failing
  // test's stderr (and exit info) so the user knows *why* it crashed. Without
  // this, RE shows only "Runtime Error" which is impossible to debug and
  // feels like a broken judge — e.g. `stack smashing detected` when a user
  // reads into a short with `scanf("%d", &short_var)` is a bug in their code,
  // not the compiler, and the stderr makes that obvious.
  const failing = Array.isArray(submission.testResults)
    ? submission.testResults.find(t => t.status && t.status !== 'AC')
    : null;
  const showDiag = failing && (failing.stderr || failing.exitCode != null || failing.stdout);
  const exitLabel = (() => {
    if (!failing) return '';
    if (failing.signal) return `terminated by signal ${failing.signal}`;
    const c = failing.exitCode;
    if (c == null) return 'killed (no exit code)';
    if (c < 0) return `terminated by signal ${-c}`;
    return `exit code ${c}`;
  })();
  return (
    <div className="training-result-card" style={{ borderColor: style.color }}>
      <div className="training-result-head">
        <strong style={{ color: style.color }}>{style.label}</strong>
        <span>{submission.totalScore} / {submission.maxScore} điểm</span>
      </div>
      {submission.compileError && (
        <pre className="training-ce">{submission.compileError}</pre>
      )}
      {showDiag && (failing.status === 'RE' || failing.status === 'SYS') && (
        <div className="training-diag">
          <div className="training-diag-head">
            <strong>Chi tiết lỗi (test #{failing.index})</strong>
            <span className="training-muted">{exitLabel}</span>
          </div>
          {failing.stderr && (
            <pre className="training-ce" style={{ whiteSpace: 'pre-wrap' }}>{failing.stderr}</pre>
          )}
          {failing.stdout && (
            <details>
              <summary className="training-muted">stdout (1 test đầu)</summary>
              <pre className="training-ce">{failing.stdout}</pre>
            </details>
          )}
        </div>
      )}
      {Array.isArray(submission.testResults) && submission.testResults.length > 0 && (
        <table className="training-test-table">
          <thead>
            <tr><th>#</th><th>Kết quả</th><th>Thời gian</th><th>Điểm</th></tr>
          </thead>
          <tbody>
            {submission.testResults.map(tr => {
              const s = STATUS_STYLE[tr.status] || STATUS_STYLE.SYS;
              return (
                <tr key={tr.index}>
                  <td>{tr.index}</td>
                  <td style={{ color: s.color }}>{s.label}</td>
                  <td>{tr.timeMs} ms</td>
                  <td>{tr.score} / {tr.maxScore}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ===== Submission list =====
const SubmissionList = ({ subs, authHeaders, show, canAdmin, onReload }) => {
  const lang = useLang();
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const bbv = useBottomBoundedViewport({ topOffset: 90, bottomGap: 16 });
  // Lock background scroll while the submission/detail modal is open.
  useBodyScrollLock(!!openId);

  const open = async (id) => {
    setOpenId(id); setDetail(null);
    try {
      const res = await fetch(`/api/training/submissions/${id}`, { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setDetail(data);
    } catch (err) { show('error', err.message); }
  };

  if (subs.length === 0) return <p className="empty-text">Chưa có bài nộp nào.</p>;

  return (
    <div>
      <table className="training-submissions-table">
        <thead>
          <tr><th>#</th><th>Người nộp</th><th>Ngôn ngữ</th><th>Kết quả</th><th>Điểm</th><th>Thời gian</th><th></th></tr>
        </thead>
        <tbody>
          {subs.map(s => {
            const style = STATUS_STYLE[s.status] || STATUS_STYLE.SYS;
            return (
              <tr key={s.id}>
                <td>#{s.id}</td>
                <td>{s.userSnapshot?.fullName || s.userSnapshot?.username || '-'}</td>
                <td>{s.language?.toUpperCase()}</td>
                <td style={{ color: style.color }}>{style.label}</td>
                <td>{s.totalScore} / {s.maxScore}</td>
                <td>{fmtDate(s.createdAt)}</td>
                <td><button className="btn btn-outline btn-sm" onClick={() => open(s.id)}>Xem</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {openId && (
        <div className="training-modal-backdrop training-modal-backdrop-bounded" style={{ top: bbv.topOffset, bottom: bbv.bottom }} onClick={() => { setOpenId(null); setDetail(null); }}>
          <div className="training-modal" onClick={e => e.stopPropagation()}>
            <div className="training-modal-head">
              <h3>Submission #{openId}</h3>
              <button className="btn btn-outline btn-sm" onClick={() => { setOpenId(null); setDetail(null); }}>✕</button>
            </div>
            {!detail && <p>Đang tải...</p>}
            {detail && (
              <>
                <SubmissionResultCard submission={detail.submission} />
                {detail.canSeeSource && detail.submission.source ? (
                  <>
                    <h4>{lang.TRAINING_SOURCE_CODE || 'Mã nguồn'}</h4>
                    <pre className="training-code">{detail.submission.source}</pre>
                  </>
                ) : (
                  <p className="training-muted">
                    🔒 Bạn chưa được phép xem mã nguồn bài này. Hãy đạt ≥ ngưỡng điểm yêu cầu trên bài toán trước.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ===== Records / submission history view =====
const RecordsView = ({ module, authHeaders, onBack, onOpen, show }) => {
  const [subs, setSubs] = useState([]);
  const [mineOnly, setMineOnly] = useState(false);

  const load = async () => {
    try {
      const q = new URLSearchParams({ moduleId: String(module.id), mine: mineOnly ? '1' : '0' });
      const res = await fetch('/api/training/submissions?' + q.toString(), { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSubs(data.submissions || []);
    } catch (err) { show('error', err.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mineOnly, module.id]);

  return (
    <div>
      <div className="training-header">
        <div>
          <button className="btn btn-outline btn-sm" onClick={onBack}>← Quay lại module</button>
          <h2 className="training-title" style={{ marginTop: 8 }}>📜 Lịch sử nộp bài — {module.name}</h2>
        </div>
        <label className="checkbox-label">
          <input type="checkbox" checked={mineOnly} onChange={e => setMineOnly(e.target.checked)} /> Chỉ bài của tôi
        </label>
      </div>
      <table className="training-submissions-table">
        <thead>
          <tr><th>#</th><th>Người nộp</th><th>Bài toán</th><th>Ngôn ngữ</th><th>Kết quả</th><th>Điểm</th><th>Thời gian</th><th></th></tr>
        </thead>
        <tbody>
          {subs.length === 0 && <tr><td colSpan="8" className="empty-row">Chưa có bài nộp nào.</td></tr>}
          {subs.map(s => {
            const style = STATUS_STYLE[s.status] || STATUS_STYLE.SYS;
            return (
              <tr key={s.id}>
                <td>#{s.id}</td>
                <td>{s.userSnapshot?.fullName || s.userSnapshot?.username || '-'}</td>
                <td>{s.problemTitle}</td>
                <td>{s.language?.toUpperCase()}</td>
                <td style={{ color: style.color }}>{style.label}</td>
                <td>{s.totalScore} / {s.maxScore}</td>
                <td>{fmtDate(s.createdAt)}</td>
                <td><button className="btn btn-outline btn-sm" onClick={() => onOpen(s.id)}>Xem</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ===== Standalone submission view =====
const SubmissionView = ({ submissionId, authHeaders, onBack, show }) => {
  const lang = useLang();
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    fetch(`/api/training/submissions/${submissionId}`, { headers: authHeaders })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (!ok) throw new Error(d.error); setDetail(d); })
      .catch(err => show('error', err.message));
    // eslint-disable-next-line
  }, [submissionId]);
  if (!detail) return <div><button className="btn btn-outline btn-sm" onClick={onBack}>← Quay lại</button><p>Đang tải...</p></div>;
  return (
    <div>
      <button className="btn btn-outline btn-sm" onClick={onBack}>← Quay lại</button>
      <h2 className="training-title" style={{ marginTop: 8 }}>
        Submission #{detail.submission.id} — {detail.problem?.title}
      </h2>
      <SubmissionResultCard submission={detail.submission} />
      {detail.canSeeSource && detail.submission.source ? (
        <>
          <h4>{lang.TRAINING_SOURCE_CODE || 'Mã nguồn'}</h4>
          <pre className="training-code">{detail.submission.source}</pre>
        </>
      ) : (
        <p className="training-muted">🔒 Bạn chưa được phép xem mã nguồn bài này.</p>
      )}
    </div>
  );
};

// ===== Leaderboard =====
const LeaderboardView = ({ module, authHeaders, onBack, show }) => {
  const [board, setBoard] = useState([]);
  useEffect(() => {
    const url = module
      ? '/api/training/leaderboard?' + new URLSearchParams({ moduleId: String(module.id) }).toString()
      : '/api/training/leaderboard';
    fetch(url, { headers: authHeaders })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (!ok) throw new Error(d.error); setBoard(d.leaderboard || []); })
      .catch(err => show('error', err.message));
    // eslint-disable-next-line
  }, [module?.id]);
  return (
    <div>
      <button className="btn btn-outline btn-sm" onClick={onBack}>← Quay lại</button>
      <h2 className="training-title" style={{ marginTop: 8 }}>
        Top giải bài{module ? ` — ${module.name}` : ' (toàn bộ)'}
      </h2>
      <table className="training-leaderboard">
        <thead><tr><th>#</th><th>Thành viên</th><th>Số bài AC</th><th>Đã thử</th><th>Tổng điểm</th></tr></thead>
        <tbody>
          {board.length === 0 && <tr><td colSpan="5" className="empty-row">Chưa có dữ liệu.</td></tr>}
          {board.map((r, i) => (
            <tr key={r.userId} className={i < 3 ? `rank-${i + 1}` : ''}>
              <td>{i + 1}</td>
              <td>
                <strong>{r.userSnapshot?.fullName || r.userSnapshot?.username || '?'}</strong>
                {r.userSnapshot?.gen && <span className="training-muted" style={{ marginLeft: 6 }}>Gen {r.userSnapshot.gen}</span>}
              </td>
              <td><strong>{r.solved}</strong></td>
              <td>{r.attempted}</td>
              <td>{r.totalScore} / {r.maxScore}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Training;
